import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, Divider, Stack, Typography,
} from '@mui/material';
import { WorkflowInstance } from '../../api/services/workflow.service';
import ToneChip from '../../components/ui/ToneChip';
import StepAnswer from './StepAnswer';
import { instanceTone, waitingOn } from './workflowUI';

// An operation's days, newest first: what each day's run asked and what
// the answer was. For a training that reads as its diary: with the
// trainer or not, how it went, and at the end the days and capacity set.
// Today's open task can be answered here, without opening the day.

const dayName = (iso: string) => new Date(iso).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
const DAY_LABEL: Record<string, string> = {
  sat: 'Sat', sun: 'Sun', mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu', fri: 'Fri',
};

/** The day in sentences, from its answers. */
function summary(run: WorkflowInstance): string[] {
  const c = run.context || {};
  const step = (id: string) => run.steps.find((s) => s.stepId === id);
  const lines: string[] = [];
  if (step('with_trainer')?.status === 'completed') lines.push(c.withTrainer === 'no' ? 'Not out today.' : 'Out with their trainer.');
  const evening = step('end_of_day');
  if (evening?.status === 'completed') {
    lines.push(c.training === 'finished' ? 'Training finished.' : 'Training continues.');
    if (evening.notes) lines.push(`“${evening.notes}”`);
  }
  const schedule = c.schedule as { days: string[]; maxStops: number; maxPackages: number } | undefined;
  if (step('set_schedule')?.status === 'completed' && schedule) {
    lines.push(`Works ${schedule.days.map((d) => DAY_LABEL[d] || d).join(', ')}; up to ${schedule.maxStops} stops and ${schedule.maxPackages} packages a day.`);
  }
  return lines;
}

const OperationDays: React.FC<{ inst: WorkflowInstance; onChanged: () => void }> = ({ inst, onChanged }) => {
  const navigate = useNavigate();
  const days = [...(inst.children || [])].reverse();
  const open = days.find((d) => d.status === 'active');
  const waiting = open?.steps.find((s) => s.stepId === open.currentStepId && s.type === 'human_task' && (s.status === 'active' || s.status === 'escalated'));

  return (
    <Box>
      {open && waiting && <StepAnswer inst={open} step={waiting} onDone={() => onChanged()} />}
      <Card sx={{ p: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>Day by day</Typography>
        {!days.length && (
          <Typography variant="body2" color="text.secondary">No day has started yet. The first one starts at 7:00.</Typography>
        )}
        {days.map((run, i) => {
          const tone = instanceTone(run);
          const lines = summary(run);
          return (
            <Box key={run._id}>
              {i > 0 && <Divider />}
              <Stack
                direction="row" gap={2} alignItems="flex-start"
                onClick={() => navigate(`/operations/${run._id}`)}
                sx={{ py: 1.25, cursor: 'pointer', borderRadius: 1, mx: -1, px: 1, '&:hover': { bgcolor: 'grey.50' } }}
              >
                <Typography variant="body2" sx={{ width: 96, flexShrink: 0, fontWeight: 600 }}>{dayName(run.startedAt)}</Typography>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  {lines.length
                    ? lines.map((l) => <Typography key={l} variant="body2">{l}</Typography>)
                    : <Typography variant="body2" color="text.secondary">{run.status === 'active' ? waitingOn(run) : 'Nothing answered.'}</Typography>}
                </Box>
                <ToneChip tone={tone.tone} label={run.status === 'cancelled' ? 'missed' : tone.label} />
              </Stack>
            </Box>
          );
        })}
      </Card>
    </Box>
  );
};

export default OperationDays;
